import { ContextData, ContextDataItem } from '../types/widget';

/**
 * Formats a value string to improve readability by adding line breaks
 * before bullet points that appear mid-text
 */
const formatValue = (value: string): string => {
  return value.replace(/([^\n])- /g, '$1\n- ');
};

/** Include valued hidden items automatically; include regular items only when selected. */
export const isFormattedContextItem = (item: ContextDataItem): boolean =>
  Boolean(item.hidden ? item.value : item.selected) &&
  typeof item.value === 'string' &&
  item.value.length > 0;

export const formatContextData = (contextData: ContextData) => {
  const sections: string[] = [];

  contextData.forEach((item) => {
    if (isFormattedContextItem(item) && typeof item.value === 'string') {
      const label =
        item.label ||
        String(item.key)
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();

      const formattedValue = formatValue(item.value);

      if (item.contextDirective) {
        sections.push(
          `${label}:\n<CONTEXT_INSTRUCTIONS>\n${item.contextDirective}\n</CONTEXT_INSTRUCTIONS>\n${formattedValue}\n`
        );
      } else {
        sections.push(`${label}:\n${formattedValue}\n`);
      }
    }

    if (item.children) {
      item.children.forEach((child) => {
        if (isFormattedContextItem(child) && typeof child.value === 'string') {
          const childLabel =
            child.label ||
            String(child.key)
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase())
              .trim();
          const formattedChildValue = formatValue(child.value);

          if (child.contextDirective) {
            sections.push(
              `${childLabel}:\n<CONTEXT_INSTRUCTIONS>\n${child.contextDirective}\n</CONTEXT_INSTRUCTIONS>\n${formattedChildValue}\n`
            );
          } else {
            sections.push(`${childLabel}:\n${formattedChildValue}\n`);
          }
        }
      });
    }
  });

  if (sections.length === 0) return '';

  const header = `--- Context Data ---
IMPORTANT: The following context data needs to be incorporated into your output. Each section may contain specific instructions on how to use the data.
`;

  return `${header}\n${sections.join('\n')}--- End Context ---`;
};
